import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type Service = {
  title: string;
  description: string;
  image: string;
};

export function ServicesSection() {
  const services = useMemo<Service[]>(
    () => [
      {
        title: "Detecção de Vazamentos em Piscinas",
        description: "Localização de vazamentos em piscinas sem necessidade de esvaziamento",
        image: "https://mnnotgqwwzenmjfkzzmy.supabase.co/storage/v1/object/public/logotipos/piscinas.png"
      },
      {
        title: "Detecção de Infiltrações",
        description: "Localização precisa de infiltrações em paredes e lajes",
        image: "https://mnnotgqwwzenmjfkzzmy.supabase.co/storage/v1/object/public/logotipos/DetectacaodeInfiltracoes.png"
      },
      {
        title: "Vazamentos em Condomínios",
        description: "Serviços especializados para condomínios residenciais e comerciais",
        image: "https://mnnotgqwwzenmjfkzzmy.supabase.co/storage/v1/object/public/logotipos/condominios.png"
      },
      {
        title: "Vazamentos em Áreas Externas",
        description: "Detecção em jardins, quintais e áreas externas",
        image: "https://mnnotgqwwzenmjfkzzmy.supabase.co/storage/v1/object/public/logotipos/externasareas.png"
      },
      {
        title: "Teste de Estanqueidade",
        description: "Testes completos de estanqueidade com laudo técnico",
        image: "https://mnnotgqwwzenmjfkzzmy.supabase.co/storage/v1/object/public/logotipos/estanqueidade.png"
      },
      {
        title: "Investigação de Conta de Água",
        description: "Investigação completa para descobrir a causa do consumo elevado e solução definitiva do problema",
        image: "https://mnnotgqwwzenmjfkzzmy.supabase.co/storage/v1/object/public/logotipos/contaagua.png"
      },
      {
        title: "Caça Vazamentos em Banheiros",
        description: "Identificação de vazamentos em banheiros com mínima quebra",
        image: "https://mnnotgqwwzenmjfkzzmy.supabase.co/storage/v1/object/public/logotipos/banheiro.png"
      },
      {
        title: "Vídeo Inspeção",
        description: "Inspeção interna de tubulações com câmeras de alta resolução para diagnóstico visual preciso",
        image: "https://mnnotgqwwzenmjfkzzmy.supabase.co/storage/v1/object/public/logotipos/videoinsp.png"
      },
      {
        title: "Desentupimento",
        description:
          "Oferecemos serviços de desentupimento com tecnologia de ponta e máxima eficiência. Utilizamos equipamentos modernos para identificar e eliminar obstruções de forma rápida, limpa e segura, sem quebrar ou danificar a estrutura",
        image: "https://mnnotgqwwzenmjfkzzmy.supabase.co/storage/v1/object/public/logotipos/desentupimento.png"
      },
      {
        title: "Caça Vazamentos em Tubulações",
        description: "Detecção precisa em tubulações hidráulicas com equipamentos de última geração",
        image: "https://mnnotgqwwzenmjfkzzmy.supabase.co/storage/v1/object/public/logotipos/tubulacoes.png"
      }
    ],
    []
  );

  const repeatedServices = useMemo(() => [...services, ...services, ...services], [services]);
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const cardWidthRef = useRef(0);
  const autoScrollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isAdjustingRef = useRef(false);
  const [isReady, setIsReady] = useState(false);

  const measureCardWidth = useCallback(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const cards = slider.querySelectorAll<HTMLDivElement>('[data-service-card]');

    if (cards.length >= 2) {
      const firstRect = cards[0].getBoundingClientRect();
      const secondRect = cards[1].getBoundingClientRect();
      cardWidthRef.current = secondRect.left - firstRect.left;
    } else if (cards.length === 1) {
      cardWidthRef.current = cards[0].getBoundingClientRect().width;
    }

    if (cardWidthRef.current === 0 && cards.length > 0) {
      requestAnimationFrame(measureCardWidth);
      return;
    }

    const totalWidth = cardWidthRef.current * services.length;
    if (totalWidth > 0) {
      slider.scrollLeft = totalWidth;
      setIsReady(true);
    }
  }, [services.length]);

  const normalizeScroll = useCallback(() => {
    const slider = sliderRef.current;
    const cardWidth = cardWidthRef.current;
    if (!slider || !cardWidth) return;

    const totalWidth = cardWidth * services.length;
    if (totalWidth === 0) return;

    if (slider.scrollLeft <= cardWidth / 2) {
      isAdjustingRef.current = true;
      slider.scrollLeft += totalWidth;
      requestAnimationFrame(() => {
        isAdjustingRef.current = false;
      });
    } else if (slider.scrollLeft >= totalWidth * 2 - cardWidth / 2) {
      isAdjustingRef.current = true;
      slider.scrollLeft -= totalWidth;
      requestAnimationFrame(() => {
        isAdjustingRef.current = false;
      });
    }
  }, [services.length]);

  const moveSlider = useCallback((direction: 1 | -1) => {
    const slider = sliderRef.current;
    const cardWidth = cardWidthRef.current;
    if (!slider || !cardWidth) return;

    slider.scrollTo({ left: slider.scrollLeft + cardWidth * direction, behavior: "smooth" });
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
      autoScrollRef.current = null;
    }
  }, []);

  const startAutoScroll = useCallback(() => {
    if (autoScrollRef.current || !isReady) return;
    const cardWidth = cardWidthRef.current;
    if (!cardWidth) return;

    autoScrollRef.current = setInterval(() => {
      moveSlider(1);
    }, 5000);
  }, [isReady, moveSlider]);

  const handlePrev = useCallback(() => {
    stopAutoScroll();
    moveSlider(-1);
    startAutoScroll();
  }, [moveSlider, startAutoScroll, stopAutoScroll]);

  const handleNext = useCallback(() => {
    stopAutoScroll();
    moveSlider(1);
    startAutoScroll();
  }, [moveSlider, startAutoScroll, stopAutoScroll]);

  useEffect(() => {
    measureCardWidth();
    window.addEventListener("resize", measureCardWidth);

    return () => {
      window.removeEventListener("resize", measureCardWidth);
    };
  }, [measureCardWidth]);

  useEffect(() => {
    if (!isReady) return;
    const slider = sliderRef.current;
    if (!slider) return;

    const handleScroll = () => {
      if (isAdjustingRef.current) return;
      normalizeScroll();
    };

    slider.addEventListener("scroll", handleScroll);
    startAutoScroll();

    return () => {
      slider.removeEventListener("scroll", handleScroll);
      stopAutoScroll();
    };
  }, [isReady, normalizeScroll, startAutoScroll, stopAutoScroll]);

  return (
    <section
      id="servicos"
      className="relative py-24 overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="absolute top-20 right-10 h-64 w-64 rounded-full bg-cyan-400/10 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-blue-600/10 blur-3xl" />
        <div className="absolute inset-y-0 left-0 w-px bg-gradient-to-b from-transparent via-blue-500/40 to-transparent" />
        <div className="absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-cyan-400/30 to-transparent" />
      </div>
      <div className="container relative mx-auto px-4">
        <div className="mx-auto mb-16 max-w-4xl text-center">
          <h2 className="mt-6 text-3xl md:text-5xl font-bold text-white">
            Nossos Serviços Exclusivos
          </h2>
          <p className="mt-4 text-lg text-slate-300">
            Soluções completas com tecnologia de ponta para localizar, diagnosticar e resolver qualquer vazamento com precisão e elegância.
          </p>
        </div>

        {/* Carrossel para dispositivos móveis */}
        <div className="relative -mx-4 pb-12 lg:hidden">
          <div className="pointer-events-none absolute inset-y-6 left-0 w-10 bg-gradient-to-r from-slate-950 via-slate-950/70 to-transparent" />
          <div className="pointer-events-none absolute inset-y-6 right-0 w-10 bg-gradient-to-l from-slate-950 via-slate-950/70 to-transparent" />
          <div className="flex snap-x snap-mandatory gap-5 overflow-x-auto px-4 pb-4">
            {services.map((service) => (
              <div
                key={service.title}
                data-service-card
                className="group relative flex w-[85vw] max-w-sm flex-shrink-0 snap-center flex-col overflow-hidden rounded-3xl border border-white/5 bg-white/5 shadow-2xl shadow-black/20 backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-cyan-400/40"
              >
                <div className="relative h-60 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-400/15 via-blue-300/10 to-transparent" />
                  <img
                    src={service.image}
                    alt={service.title}
                    className="h-full w-full object-cover object-center transition-all duration-500 group-hover:scale-110 brightness-110 contrast-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/55 via-slate-900/15 to-transparent" />
                  <div className="absolute left-4 top-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white/90 text-blue-600 shadow-lg shadow-blue-900/30 transition-all duration-300 group-hover:scale-110">
                    <i className="ri-service-line text-xl" />
                  </div>
                </div>

                <div className="flex h-full flex-col gap-4 p-6">
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-white transition-colors group-hover:text-cyan-200">
                      {service.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-slate-300">
                      {service.description}
                    </p>
                  </div>
                  <a
                    href="https://wa.me/5511948948561?text=Ol%C3%A1%2C%20gostaria%20de%20solicitar%20um%20or%C3%A7amento%20para%20detec%C3%A7%C3%A3o%20de%20vazamento.%20Pode%20me%20ajudar%3F"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-auto inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full border border-cyan-400/70 bg-gradient-to-r from-cyan-500/85 to-blue-600/85 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-900/40 transition-all duration-300 hover:from-cyan-400 hover:to-blue-500 hover:shadow-blue-800/60"
                  >
                    <i className="ri-whatsapp-line text-base" />
                    Solicitar Orçamento
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Carrossel de Serviços com movimento contínuo */}
        <div className="relative mx-0 hidden lg:block">
          <div className="relative mx-auto max-w-6xl">
            <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-slate-950 via-slate-950/70 to-transparent"></div>
            <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-slate-950 via-slate-950/70 to-transparent"></div>

            <button
              type="button"
              onClick={handlePrev}
              aria-label="Serviço anterior"
              className="group absolute left-0 top-1/2 z-20 hidden -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-white/10 p-3 text-white shadow-lg backdrop-blur transition-all duration-300 hover:border-cyan-400/50 hover:bg-cyan-500/30 hover:text-cyan-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 lg:flex"
            >
              <i className="ri-arrow-left-s-line text-2xl" />
            </button>

            <button
              type="button"
              onClick={handleNext}
              aria-label="Próximo serviço"
              className="group absolute right-0 top-1/2 z-20 hidden -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-white/10 p-3 text-white shadow-lg backdrop-blur transition-all duration-300 hover:border-cyan-400/50 hover:bg-cyan-500/30 hover:text-cyan-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 lg:flex"
            >
              <i className="ri-arrow-right-s-line text-2xl" />
            </button>

            <div className="overflow-hidden sm:mx-8 lg:mx-16">
              <div
                ref={sliderRef}
                className="flex gap-10 overflow-x-scroll scroll-smooth py-4 no-scrollbar"
                onMouseEnter={stopAutoScroll}
                onMouseLeave={startAutoScroll}
              >
                {repeatedServices.map((service, index) => (
                  <div
                    key={`${service.title}-${index}`}
                    data-service-card
                    className="group relative flex w-[22rem] flex-shrink-0 flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/5 shadow-[0_20px_45px_-20px_rgba(15,118,230,0.65)] backdrop-blur transition-all duration-500 hover:-translate-y-2 hover:border-cyan-400/40 hover:shadow-[0_30px_60px_-25px_rgba(59,130,246,0.75)]"
                  >
                    <div className="relative h-64 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/15 via-blue-400/10 to-transparent" />
                      <img
                        src={service.image}
                        alt={service.title}
                        className="h-full w-full object-cover object-center transition-all duration-700 group-hover:scale-110 group-hover:rotate-1 brightness-110 contrast-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-slate-900/20 to-transparent" />
                      <div className="absolute right-6 top-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-blue-600 shadow-xl shadow-blue-900/40 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:scale-110">
                        <i className="ri-service-line text-2xl" />
                      </div>
                    </div>

                    <div className="flex h-full flex-col p-8">
                      <div className="space-y-4">
                        <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] text-cyan-200/80">
                          Serviço {(index % services.length) + 1 < 10 ? `0${(index % services.length) + 1}` : (index % services.length) + 1}
                        </span>
                        <h3 className="text-2xl font-semibold text-white transition-colors group-hover:text-cyan-200">
                          {service.title}
                        </h3>
                        <p className="text-base leading-relaxed text-slate-300">
                          {service.description}
                        </p>
                      </div>
                      <a
                        href="https://wa.me/5511948948561?text=Ol%C3%A1%2C%20gostaria%20de%20solicitar%20um%20or%C3%A7amento%20para%20detec%C3%A7%C3%A3o%20de%20vazamento.%20Pode%20me%20ajudar%3F"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-auto inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-cyan-400/70 bg-gradient-to-r from-cyan-500/80 via-blue-600/80 to-indigo-600/80 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-900/50 transition-all duration-300 hover:from-cyan-400 hover:via-blue-500 hover:to-indigo-500 hover:shadow-blue-800/70"
                      >
                        Solicitar Orçamento
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CTA Principal */}
        <div className="text-center mt-12">
          <a
            href="https://wa.me/5511948948561?text=Ol%C3%A1%2C%20gostaria%20de%20solicitar%20um%20or%C3%A7amento%20para%20detec%C3%A7%C3%A3o%20de%20vazamento.%20Pode%20me%20ajudar%3F"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-full flex-wrap items-center justify-center gap-2 rounded-2xl border border-cyan-400/70 bg-gradient-to-r from-cyan-500 via-blue-600 to-indigo-600 px-6 py-4 text-sm font-semibold text-white shadow-xl shadow-blue-900/40 transition-all duration-300 hover:from-cyan-400 hover:via-blue-500 hover:to-indigo-500 hover:shadow-blue-900/70 sm:w-auto sm:text-base"
          >
            <i className="ri-whatsapp-line text-lg sm:text-xl" />
            <span className="leading-snug">Solicitar Orçamento para Qualquer Serviço</span>
          </a>
        </div>
      </div>
    </section>
  );
}
